import { Col, Row } from 'antd';
import { Pin } from 'types';
import { formatToDate } from 'utils/formatToDate';
import { formatToProgressValue } from 'utils/formatToProgressValue';

import { DocumentDetailsModel } from 'api/documents/documents.model';
import { BasicCard } from 'components/BasicCard';
import CardRow from 'components/CardRow';
import CompositionRow from 'components/CompositionRow';
import Detail from 'components/Detail';
import Map from 'components/Map';
import { extractCoordinatesFromProduct } from 'components/Map/utils';
import SectionTitle from 'components/SectionTitle';
import SubTitle from 'components/SubTitle';
import useCertificateHelper from 'hooks/useCertificateHelper';
import {
  ADDITIONAL_DETAILS,
  CertificateDetailsModel,
  NESTED_DETAILS,
  PRODUCER_DETAILS,
} from 'models/certificate.model';

import Progress from '../components/Progress';
import RelatedCertificates from '../components/RelatedCertificates';
import Template from '../components/Template';

const BatteryPacksRecycling = ({
  additionalDetails = {} as CertificateDetailsModel,
  ...document
}: DocumentDetailsModel<CertificateDetailsModel>) => {
  const { getLabel, getValueByLabel } = useCertificateHelper();

  const pins: Pin[] = [];

  if (additionalDetails[ADDITIONAL_DETAILS.PRODUCER]) {
    const coordinates = extractCoordinatesFromProduct(
      additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
    );

    if (coordinates) {
      pins.push({
        coordinates,
        iconPath: '/pin-blue.svg',
      });
    }
  }

  return (
    <Template
      type={document.category}
      jobId={document.jobId}
      favourite={document.favourite}
      batchCard={
        <>
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_PACK_SERIAL_NUMBER,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail label={getLabel('passportId')} value={document.documentBlockchainAddress} />
          <CardRow>
            <Detail
              label={getLabel(ADDITIONAL_DETAILS.ASSEMBLY_NUMBER)}
              value={additionalDetails[ADDITIONAL_DETAILS.ASSEMBLY_NUMBER]}
            />
            <Detail
              label={getLabel(ADDITIONAL_DETAILS.CONTROL_NUMBER)}
              value={additionalDetails[ADDITIONAL_DETAILS.CONTROL_NUMBER]}
            />
          </CardRow>
        </>
      }
      detailsCard={
        <CardRow>
          <Detail
            label={getLabel(NESTED_DETAILS.EVMODEL)}
            value={getValueByLabel(
              NESTED_DETAILS.EVMODEL,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PRODUCERS)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_PRODUCERS,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PRODUCTION_DATE)}
            value={formatToDate(
              getValueByLabel(
                NESTED_DETAILS.BATTERY_PRODUCTION_DATE,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_CELL_MODEL)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_CELL_MODEL,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel(NESTED_DETAILS.BATTERY_PACKS_GLOBAL_TRADE_IDENTIFICATION_NUMBER)}
            value={getValueByLabel(
              NESTED_DETAILS.BATTERY_PACKS_GLOBAL_TRADE_IDENTIFICATION_NUMBER,
              additionalDetails[ADDITIONAL_DETAILS.DETAILS]
            )}
          />
          <Detail
            label={getLabel('baterryPackTechnicalReviewCertificateUID')}
            value={additionalDetails.productId}
          />
        </CardRow>
      }
    >
      <Row gutter={[16, 16]}>
        <Col md={24} lg={10}>
          <SectionTitle>{getLabel('recyclingDescription')}</SectionTitle>
          <CardRow>
            <Detail
              label={getLabel(NESTED_DETAILS.RECYCLE_DATA)}
              value={formatToDate(
                getValueByLabel(
                  NESTED_DETAILS.RECYCLE_DATA,
                  additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                )
              )}
            />
            <Detail
              label={getLabel(NESTED_DETAILS.RECYCLE_PERCENT)}
              value={
                <Progress
                  value={formatToProgressValue(
                    getValueByLabel(
                      NESTED_DETAILS.RECYCLE_PERCENT,
                      additionalDetails[ADDITIONAL_DETAILS.DETAILS]
                    )
                  )}
                />
              }
            />
            <Detail
              label={getLabel(NESTED_DETAILS.RECYCLE_MATERIAL)}
              value={getValueByLabel(
                NESTED_DETAILS.RECYCLE_MATERIAL,
                additionalDetails[ADDITIONAL_DETAILS.DETAILS]
              )}
            />
          </CardRow>
        </Col>
        <Col md={24} lg={14}>
          <RelatedCertificates
            additionalDetails={additionalDetails}
            relatedFields={['batteryCellsBatchNumber', 'batteryPackSerialNumber']}
          />
        </Col>
      </Row>

      <SectionTitle>{getLabel('specification')}</SectionTitle>
      <SubTitle>{getLabel('generalInfo')}</SubTitle>
      <CompositionRow
        composition={additionalDetails[ADDITIONAL_DETAILS.DETAILS]}
        labels={[
          NESTED_DETAILS.CELLS_SETUP,
          NESTED_DETAILS.WEIGHT,
          NESTED_DETAILS.GRAVIMETRIC_ENERGY_DENSITY,
        ]}
      />
      <SectionTitle>{getLabel('recyclingCompany')}</SectionTitle>
      <Map
        pins={pins}
        card={
          <BasicCard>
            <SectionTitle noMargin dividerMargin="1rem">
              {getLabel(PRODUCER_DETAILS.COMPANY)}
            </SectionTitle>
            <Detail
              label={getLabel('recycler')}
              value={getValueByLabel(
                PRODUCER_DETAILS.COMPANY,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel(PRODUCER_DETAILS.COMPANY_HEADQUATERS)}
              value={getValueByLabel(
                PRODUCER_DETAILS.COMPANY_HEADQUATERS,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
            <Detail
              label={getLabel(PRODUCER_DETAILS.RECYCLER_LOCATION)}
              value={getValueByLabel(
                PRODUCER_DETAILS.RECYCLER_LOCATION,
                additionalDetails[ADDITIONAL_DETAILS.PRODUCER]
              )}
            />
          </BasicCard>
        }
      />
    </Template>
  );
};

export default BatteryPacksRecycling;
